import { useTranslation } from 'react-i18next'
import { PhoneOff, Wallet } from 'lucide-react'

import { Alert } from '../ui/Alert'
import { formatCurrency } from '../../utils/format'
import { TWILIO_BILLING_CONSOLE_URL, type TwilioBalanceStatus } from '../../services/twilioBalance'
import { bannerVariant } from './bannerVisibility'

/**
 * BANDEAU DE SOLDE TWILIO (LOT #142 / E-20).
 *
 * Vit dans la `NotificationRegion` de l'AppShell (zone H9), à côté du bandeau
 * d'essai. PERSISTANT tant que l'état dure, REFERMABLE pour la session (la
 * croix vient d'`Alert`, la mémoire de fermeture du hook
 * `useTwilioBalanceBanner`) — il réapparaît si l'état empire.
 *
 * Ce composant ne décide PAS s'il doit exister : c'est `bannerVariant`, lue
 * par le parent, qui tranche (un élément rendu à `null` compterait quand même
 * dans `Children.toArray` et ferait exister la zone à vide — garde H9). Le
 * `null` d'ici n'est qu'une seconde ceinture.
 *
 * Tons (design system §5sexies) : WARN → `warning` (solde bas = à surveiller) ;
 * CRITICAL et SUSPENDED → `danger`. CRITICAL en rouge est un choix produit du
 * mandat (E-20 : à 15 USD il reste quelques heures de plateau) plutôt que la
 * lettre du §5sexies (« un solde bas est un warning ») — assumé, consigné.
 *
 * Le solde ne concerne QUE le super-admin (décision utilisateur #142) : WARN et
 * CRITICAL ne s'affichent qu'à lui ; les autres rôles ne voient qu'UN message,
 * en SUSPENDED seulement (« Téléphonie indisponible — contactez l'assistance »),
 * sans description ni montant ni lien.
 *
 * Le lien vers la console Twilio Billing est l'UNIQUE action, et seul le
 * super-admin le voit : c'est lui qui recharge. Un lien texte, pas un bouton :
 * le geste se fait hors du CRM (design system §5sexies, « une alerte porte une
 * action au plus »).
 */
export interface TwilioBalanceBannerProps {
  status: TwilioBalanceStatus
  isSuperAdmin: boolean
  onDismiss?: () => void
}

export function TwilioBalanceBanner({ status, isSuperAdmin, onDismiss }: TwilioBalanceBannerProps) {
  const { t } = useTranslation('shell')
  const variant = bannerVariant(status.state, isSuperAdmin)
  if (!variant) return null

  const amount = formatCurrency(status.balance, status.currency)
  const consoleLink = (
    <a
      href={TWILIO_BILLING_CONSOLE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary-400 underline underline-offset-2 hover:text-primary-300"
    >
      {t('twilioBalance.consoleLink')}
    </a>
  )

  let title: string
  let description: string | null
  switch (status.state) {
    case 'WARN':
      title = t('twilioBalance.warn.title', { amount })
      description = t('twilioBalance.warn.description')
      break
    case 'CRITICAL':
      // Super-admin seulement (bannerVariant rend null sinon).
      title = t('twilioBalance.critical.title', { amount })
      description = t('twilioBalance.critical.description')
      break
    case 'SUSPENDED':
    default:
      if (variant.detailed) {
        title = t('twilioBalance.suspended.title')
        description = t('twilioBalance.suspended.description')
      } else {
        // Autres rôles : UN seul message, sans description, sans lien, sans
        // montant (décision utilisateur #142).
        title = t('twilioBalance.suspended.publicTitle')
        description = null
      }
  }

  return (
    <Alert
      tone={variant.tone}
      icon={status.state === 'SUSPENDED' ? PhoneOff : Wallet}
      title={title}
      onDismiss={onDismiss}
      dismissLabel={t('twilioBalance.dismiss')}
      className={`twilio-balance-banner twilio-balance-banner--${status.state.toLowerCase()}`}
    >
      {description}
      {variant.detailed && <> {consoleLink}</>}
    </Alert>
  )
}

export default TwilioBalanceBanner
